import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import type { ManualTaskInput, LiveSection } from '../../types/taskList';

interface AddManualTaskDialogProps {
  open: boolean;
  sectionId: string;
  sections: LiveSection[];
  onConfirm: (input: ManualTaskInput) => void;
  onClose: () => void;
}

export const AddManualTaskDialog: React.FC<AddManualTaskDialogProps> = ({
  open,
  sectionId,
  sections,
  onConfirm,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState(sectionId);
  const [description, setDescription] = useState('');
  const [noteRequired, setNoteRequired] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setSelectedSectionId(sectionId);
      setDescription('');
      setNoteRequired(false);
    }
  }, [open, sectionId]);

  const isValid = title.trim().length > 0 && selectedSectionId.length > 0;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({
      title: title.trim(),
      sectionId: selectedSectionId,
      description: description.trim() || undefined,
      noteRequired: noteRequired || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Task</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            fullWidth
            autoFocus
            size="small"
          />

          <TextField
            select
            label="Section"
            value={selectedSectionId}
            onChange={e => setSelectedSectionId(e.target.value)}
            required
            fullWidth
            size="small"
          >
            {sections.map(s => (
              <MenuItem key={s.sectionId} value={s.sectionId}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={noteRequired}
                onChange={e => setNoteRequired(e.target.checked)}
              />
            }
            label="Note required to complete this task"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!isValid}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
